export interface LicenceBooleans {
    is_owner: boolean;
    is_finance: boolean;
    is_commercial: boolean;
    is_crm_deletable: boolean;
    is_assistant: boolean;
    is_half_licence: boolean;
    is_free_licence: boolean;
    documentation_only: boolean;
    cms_id: number | undefined;
}

export interface CMS {
    id: number;
}

export interface User {
    is_owner: boolean;
    is_finance: boolean;
    is_commercial: boolean;
    is_assistant: boolean;
    is_half_licence: boolean;
    is_free_licence: boolean;
    documentation_only: boolean;
    cms: CMS | number | null;
}

// Types de licence que seuls les superusers FacilitaTrip peuvent attribuer
export const SUPERUSER_ONLY_LICENCE_TYPES = ['OWNER', 'FREE'];

export const canAssignLicenceType = (type: string, is_superuser: boolean): boolean => {
    return is_superuser || !SUPERUSER_ONLY_LICENCE_TYPES.includes(type);
};

// Types de licence auxquels l'acces Finance peut se cumuler (cf. matrice des licences)
export const FINANCE_ADDON_LICENCE_TYPES = ['OWNER'];

export const canHaveFinanceAddon = (type: string): boolean => FINANCE_ADDON_LICENCE_TYPES.includes(type);

// Un user cumule quand il porte le flag du type de base ET is_finance
export const hasFinanceAddon = (user: User | null): boolean => {
    if (!user) return false;
    return canHaveFinanceAddon(getCurrentLicenceType(user)) && !!user.is_finance;
};

export const getLicenceBooleans = (
    type: string,
    has_cms: boolean,
    cms: CMS | null,
    finance_addon = false
): LicenceBooleans => ({
    is_owner: type === 'OWNER',
    is_finance: type === 'FINANCE' || (canHaveFinanceAddon(type) && finance_addon),
    is_commercial: type === 'COMMERCIAL',
    is_crm_deletable: type === 'CRM',
    is_assistant: type === 'ASSISTANT',
    is_half_licence: type === 'HALF_LICENCE',
    is_free_licence: type === 'FREE',
    documentation_only: type === 'DOCUMENTATION',
    cms_id: (has_cms && type === 'CMS' && cms) ? cms.id : undefined
});

// Types de licence dont le role est attribue automatiquement par l'API (DOCUMENTATION = API)
export const AUTO_ROLE_LICENCE_TYPES = ['FINANCE', 'COMMERCIAL', 'ASSISTANT', 'DOCUMENTATION'];

export const hasAutoAssignedRole = (type: string): boolean => AUTO_ROLE_LICENCE_TYPES.includes(type);

const LICENCE_TYPE_FLAGS: Record<string, string> = {
    OWNER: 'is_owner',
    FINANCE: 'is_finance',
    COMMERCIAL: 'is_commercial',
    CRM: 'is_crm_user',
    ASSISTANT: 'is_assistant',
    HALF_LICENCE: 'is_half_licence',
    FREE: 'is_free_licence',
    DOCUMENTATION: 'documentation_only'
};

// Le type de licence n'est pas un champ : il se filtre via les booleans du user
export const getLicenceTypeFilterParams = (type: string): Record<string, boolean> | undefined => {
    if (type === '' || type === 'ALL') {
        return undefined;
    }
    if (type === 'CMS') {
        return { cms__isnull: false };
    }
    if (type === 'BO') {
        const params: Record<string, boolean> = { cms__isnull: true };
        Object.values(LICENCE_TYPE_FLAGS).forEach((flag) => { params[flag] = false; });
        return params;
    }
    const flag = LICENCE_TYPE_FLAGS[type];
    return flag ? { [flag]: true } : undefined;
};

export const getCurrentLicenceType = (user: User | null): string => {
    if (!user) return 'BO';
    if (user.is_owner) return 'OWNER';
    if (user.is_finance) return 'FINANCE';
    if (user.is_commercial) return 'COMMERCIAL';
    if (user.is_assistant) return 'ASSISTANT';
    if (user.is_half_licence) return 'HALF_LICENCE';
    if (user.is_free_licence) return 'FREE';
    if (user.documentation_only) return 'DOCUMENTATION';
    if (user.cms) return 'CMS';
    return 'BO';
};

export const getLicenceTypeLabel = (type: string, t: (key: string) => string): string => {
    switch (type) {
        case 'BO': return t('shared.licence-bo');
        case 'CMS': return t('shared.licence-cms');
        case 'CRM': return t('shared.licence-crm');
        case 'ASSISTANT': return t('shared.licence-assistant');
        case 'HALF_LICENCE': return t('shared.licence-half');
        case 'COMMERCIAL': return t('shared.licence-commercial');
        case 'OWNER': return t('shared.licence-owner');
        case 'FINANCE': return t('shared.licence-finance');
        case 'DOCUMENTATION': return t('shared.licence-api');
        case 'FREE': return t('shared.licence-free');
        default: return type;
    }
};

// Licences actives d'un user : le type de base, plus FINANCE quand il est cumule
export const getUserLicenceTypes = (user: User | null): string[] => {
    const types = [getCurrentLicenceType(user)];
    if (hasFinanceAddon(user)) types.push('FINANCE');
    return types;
};

// Libelle affiche pour un user : ajoute l'acces Finance cumule au type de base
export const getUserLicenceLabel = (user: User | null, t: (key: string) => string): string => {
    return getUserLicenceTypes(user).map((type) => getLicenceTypeLabel(type, t)).join(' + ');
};
